import { PartnerSettingValue, RecordFolderAssignment } from '@provision/entity-types';
import { portalPaths } from '@provision/portal-routes';
import {
  NavbarItemId,
  PLATFORM_PARTNER_HIDDEN_MENU_ITEMS,
} from 'src/components/Navbar/constants';
import { type NavigationDataItem } from 'src/components/Navbar/use-navigation-items';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  AllowedRuleSet,
  isSettingsEnabled,
  MatchType,
  PartnerGroupRules,
  RecordFolder,
  UserGroupRules,
} from 'src/graphql';
import {
  CustomView,
  EntityRulesData,
  RULES_MAP_PER_VIEW_ENTITY,
} from 'src/graphql/entities/custom-view';
import { EntityNames } from 'src/graphql/types';

const ICON_TO_ENTITY = {
  CustomView: 'star',
  Conversation: 'message-circle',
};

const isPlatformPartnerViewsItem = (
  itemId: string,
  selectedPartnerId: number | null,
): boolean =>
  selectedPartnerId === PLATFORM_PARTNER_ID && itemId === (NavbarItemId.Views as string);

export const mapAssignmentToNavItem = (data: {
  assignment: RecordFolderAssignment;
  folder: RecordFolder;
  customViews: CustomView[];
  selectedPartnerId: string | number | null;
  deleteAssignment?: (assignment: RecordFolderAssignment) => Promise<void>;
}): NavigationDataItem => {
  const { assignment, customViews, folder, selectedPartnerId, deleteAssignment } = data;

  let path = '';
  let icon = '';
  let title = assignment.title;
  let restrictions: EntityRulesData | undefined;

  if (assignment.entity === 'CustomView') {
    const view = customViews.find((v) => v.customViewId === assignment.entityId);
    if (view) {
      path = portalPaths.entity.selected(view.entityName, view.customViewId);
      icon = view.icon;
      title = view.name;
      restrictions = RULES_MAP_PER_VIEW_ENTITY[view.entityName] ?? {};
    }
  } else if (assignment.entity === 'Conversation') {
    path = portalPaths.conversation.full(assignment.entityId);
  }

  if (!restrictions) {
    restrictions =
      RULES_MAP_PER_VIEW_ENTITY[assignment.entity.toLowerCase() as EntityNames] ?? {};
  }

  return {
    ...restrictions,
    id: assignment.recordFolderAssignmentId,
    rawAssignmentItem: assignment,
    title,
    iconData: {
      name:
        icon ||
        ICON_TO_ENTITY[assignment.entity as keyof typeof ICON_TO_ENTITY] ||
        folder.icon ||
        'folder',
      library: 'lucide',
    },
    path,
    customViewId: assignment.entityId,
    menuItems:
      deleteAssignment && selectedPartnerId === assignment.partnerId
        ? [
            {
              icon: 'trash-2',
              iconLibrary: 'lucide',
              label: 'Remove from folder',
              onClick: () => {
                void deleteAssignment(assignment);
              },
            },
          ]
        : undefined,
  };
};

export const filterItemsByAccess = (data: {
  items: NavigationDataItem[];
  checkValidity: (
    rules: (PartnerGroupRules | UserGroupRules | AllowedRuleSet)[],
    opts: { matchType: MatchType },
  ) => boolean;
  isWorkingWithPartner: boolean;
  isMobile: boolean;
  partnerSettings: Partial<PartnerSettingValue>[];
  selectedPartnerId: number | null;
}): NavigationDataItem[] => {
  const {
    items,
    checkValidity,
    isWorkingWithPartner,
    isMobile,
    partnerSettings,
    selectedPartnerId,
  } = data;

  return items.filter((item) => {
    if (
      selectedPartnerId === PLATFORM_PARTNER_ID &&
      PLATFORM_PARTNER_HIDDEN_MENU_ITEMS.includes(item.id)
    ) {
      return false;
    }

    let available = true;

    if (
      item.allowedRules !== undefined &&
      !isPlatformPartnerViewsItem(item.id, selectedPartnerId)
    ) {
      const matchType: MatchType = item.matchType ?? 'any';
      available = checkValidity(item.allowedRules, { matchType });
    }

    if (available && item.disallowedRules !== undefined) {
      const matchType: MatchType = item.matchType ?? 'any';
      const isDisallowed = checkValidity(item.disallowedRules, { matchType });
      available = !isDisallowed;
    }

    if (available && item.disallowedPartnerSettings?.length) {
      const settingsDisallowMatch =
        item.disallowedPartnerSettings.some((settingId) =>
          partnerSettings.some((data) =>
            isSettingsEnabled(settingId, data as PartnerSettingValue),
          ),
        ) ?? false;

      return !settingsDisallowMatch;
    }

    if (!isWorkingWithPartner && available && 'isMultitenancyAllowed' in item) {
      available = Boolean(item.isMultitenancyAllowed);
    }

    if (isMobile && available && 'isMobileSupported' in item) {
      available = Boolean(item.isMobileSupported);
    }
    return available;
  });
};
